import { useState, useEffect } from "react";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import AppContext from "./utils/appContext";
import Layout from "./components/layout";
import NotFound from "./pages/notfound";
import Files from "./pages/files";
import Email from "./pages/email";
import Users from "./pages/users";
import Contacts from "./pages/contacts";
import PressReleases from "./pages/pressreleases";
import ContactPage from "./pages/contactpage";

import Unsubscribe from "./pages/unsubsrcibe";
import Bin from "./pages/bin";
import ActivateAccount from "./pages/activate-account";
import RequestPasswordReset from "./pages/request-password-reset";
import ResetPassword from "./pages/reset-password";
import Login from "./pages/login";
import Home from "./pages/home";
import { ConfirmDialogHost, ToastViewport } from "./components/ui";

interface RequireAuthProps {
  children: JSX.Element;
}

const RequireAuth = ({ children }: RequireAuthProps) => {
  const location = useLocation();
  const storedJWT = localStorage.getItem("jwt");

  if (!storedJWT) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
};

function App() {
  const [isLoggedIn, toggleLoggedIn] = useState<boolean>(false);
  const [orderList, setOrderList] = useState<any[]>([]);

  const updateLoginStatus = (status: boolean) => {
    toggleLoggedIn(status);
  };

  const addToOrderList = (order: any) => {
    const currentOrderList = orderList;

    if (orderList.length === 0) {
      setOrderList([...orderList, order]);
    } else {
      const index = currentOrderList.findIndex(
        (o) => o.product.ID === order.product.ID,
      );

      if (index === -1) {
        setOrderList([...orderList, order]);
      } else {
        const originalQuantiy = currentOrderList[index].quantity;
        const newQuantity = originalQuantiy + order.quantity;

        // Create a new array with the updated item
        const newOrderList = currentOrderList.map((item, i) =>
          i === index ? { ...item, quantity: newQuantity } : item,
        );

        setOrderList(newOrderList);
      }
    }
  };

  const removeFromOrderList = (product_id: number) => {
    if (orderList.length === 0) return;
    const found = orderList.some((o) => o.product.ID === product_id);
    if (found) {
      setOrderList(orderList.filter((o) => o.product.ID !== product_id));
    }
  };

  const emptyOrderList = () => {
    setOrderList([]);
  };
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <AppContext.Provider
      value={{
        isLoggedIn,
        updateLoginStatus,
        orderList,
        addToOrderList,
        removeFromOrderList,
        emptyOrderList,
      }}
    >
      <Routes>
        <Route path="/login" element={<Login />}></Route>
        <Route path="/activate-account" element={<ActivateAccount />}></Route>
        <Route
          path="/request-password-reset"
          element={<RequestPasswordReset />}
        ></Route>
        <Route path="/reset-password" element={<ResetPassword />}></Route>
        <Route path="/unsubscribe" element={<Unsubscribe />}></Route>
        <Route
          path="/"
          element={
            <Layout>
              <Home />
            </Layout>
          }
        />
        {process.env.REACT_APP_FILES === "true" ? (
          <Route
            path="/files"
            element={
              <RequireAuth>
                <Layout>
                  <Files />
                </Layout>
              </RequireAuth>
            }
          />
        ) : null}
        {process.env.REACT_APP_FILES === "true" ? (
          <Route
            path="/files/*"
            element={
              <RequireAuth>
                <Layout>
                  <Files />
                </Layout>
              </RequireAuth>
            }
          />
        ) : null}
        <Route
          path="/users"
          element={
            <RequireAuth>
              <Layout>
                <Users />
              </Layout>
            </RequireAuth>
          }
        />
        {process.env.REACT_APP_CONTACTS === "true" ? (
          <Route
            path="/contacts"
            element={
              <RequireAuth>
                <Layout>
                  <Contacts />
                </Layout>
              </RequireAuth>
            }
          />
        ) : null}
        {process.env.REACT_APP_CONTACTS === "true" ? (
          <Route
            path="/contacts/:id"
            element={
              <RequireAuth>
                <Layout>
                  <ContactPage />
                </Layout>
              </RequireAuth>
            }
          />
        ) : null}
        {process.env.REACT_APP_EMAIL === "true" ? (
          <Route
            path="/email"
            element={
              <RequireAuth>
                <Layout>
                  <Email />
                </Layout>
              </RequireAuth>
            }
          />
        ) : null}
        <Route
          path="/bin"
          element={
            <RequireAuth>
              <Layout>
                <Bin />
              </Layout>
            </RequireAuth>
          }
        />
        {process.env.REACT_APP_PRESS_RELEASE === "true" ? (
          <Route
            path="/press-releases"
            element={
              <RequireAuth>
                <Layout>
                  <PressReleases />
                </Layout>
              </RequireAuth>
            }
          />
        ) : null}
        <Route path="*" element={<NotFound />} /> {/* 404 Route */}
      </Routes>
      <ConfirmDialogHost />
      <ToastViewport />
    </AppContext.Provider>
  );
}

export default App;
